'use client';

import React from 'react';
import Link from 'next/link';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '@heroui/modal';
import { Project } from '@/data/projects';
import { getTechIcon } from '@/lib/utils/techIcons';
import {
  Sparkles,
  Blocks,
  Barcode,
  HousePlug,
  CarFront,
  Stethoscope,
  ChartLine,
  Lock,
  ExternalLink,
} from 'lucide-react';
import Image from 'next/image';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

// Function to render different pattern types
const renderPattern = (pattern: Project['pattern']) => {
  switch (pattern) {
    case 'circles':
      return (
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute top-4 left-4 h-3 w-3 animate-spin rounded-full bg-purple-400"
            style={{ animationDuration: '3s' }}
          ></div>
          <div
            className="absolute top-12 right-8 h-2 w-2 animate-bounce rounded-full bg-purple-300"
            style={{ animationDelay: '0.5s', animationDuration: '1s' }}
          ></div>
          <div
            className="absolute bottom-8 left-12 h-4 w-4 animate-ping rounded-full bg-purple-400"
            style={{ animationDelay: '1s', animationDuration: '2s' }}
          ></div>
          <div
            className="absolute right-4 bottom-16 h-2 w-2 animate-bounce rounded-full bg-purple-300"
            style={{ animationDelay: '1.5s', animationDuration: '0.8s' }}
          ></div>
          <div
            className="absolute top-20 left-20 h-1 w-1 animate-spin rounded-full bg-pink-400"
            style={{ animationDelay: '2s', animationDuration: '4s' }}
          ></div>
          <div
            className="absolute top-32 right-16 h-2 w-2 animate-bounce rounded-full bg-blue-300"
            style={{ animationDelay: '2.5s', animationDuration: '1.2s' }}
          ></div>
        </div>
      );

    case 'grid':
      return (
        <div className="absolute inset-0 opacity-20">
          <svg
            className="h-full w-full animate-pulse"
            style={{ animationDuration: '2s' }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="grid-modal"
                x="0"
                y="0"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  className="text-slate-400"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-modal)" />
          </svg>
        </div>
      );

    case 'dots':
      return (
        <div className="absolute inset-0 opacity-20">
          <svg
            className="h-full w-full animate-pulse"
            style={{ animationDuration: '1.5s' }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="dots-modal"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="1"
                  fill="currentColor"
                  className="text-green-400"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots-modal)" />
          </svg>
        </div>
      );

    case 'waves':
      return (
        <div className="absolute inset-0 opacity-20">
          <svg
            className="h-full w-full animate-pulse"
            style={{ animationDuration: '3s' }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="waves"
                x="0"
                y="0"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 0 30 Q 15 20 30 30 T 60 30"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  className="text-orange-400"
                />
                <path
                  d="M 0 40 Q 15 30 30 40 T 60 40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  className="text-red-400"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#waves)" />
          </svg>
        </div>
      );

    case 'hexagons':
      return (
        <div className="absolute inset-0 opacity-20">
          <svg
            className="h-full w-full animate-pulse"
            style={{ animationDuration: '2.5s' }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="hexagons"
                x="0"
                y="0"
                width="50"
                height="50"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 25 5 L 45 15 L 45 35 L 25 45 L 5 35 L 5 15 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  className="text-purple-400"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hexagons)" />
          </svg>
        </div>
      );

    case 'stars':
      return (
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute top-6 left-6 animate-spin text-yellow-400"
            style={{ animationDuration: '4s' }}
          >
            ★
          </div>
          <div
            className="absolute top-16 right-12 animate-bounce text-blue-400"
            style={{ animationDelay: '0.5s', animationDuration: '0.8s' }}
          >
            ★
          </div>
          <div
            className="absolute bottom-12 left-16 animate-ping text-cyan-400"
            style={{ animationDelay: '1s', animationDuration: '2s' }}
          >
            ★
          </div>
          <div
            className="absolute right-8 bottom-20 animate-bounce text-indigo-400"
            style={{ animationDelay: '1.5s', animationDuration: '1.2s' }}
          >
            ★
          </div>
          <div
            className="absolute top-24 left-32 animate-spin text-purple-400"
            style={{ animationDuration: '6s' }}
          >
            ★
          </div>
          <div
            className="absolute top-36 right-24 animate-bounce text-pink-400"
            style={{ animationDelay: '2.5s', animationDuration: '0.9s' }}
          >
            ★
          </div>
        </div>
      );

    default:
      return (
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute top-4 left-4 h-3 w-3 animate-spin rounded-full bg-purple-400"
            style={{ animationDuration: '3s' }}
          ></div>
          <div
            className="absolute top-12 right-8 h-2 w-2 animate-bounce rounded-full bg-purple-300"
            style={{ animationDelay: '0.5s', animationDuration: '0.8s' }}
          ></div>
        </div>
      );
  }
};

const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  if (!project) return null;

  // Helper function to render Lucide React icons
  const renderIcon = (iconName: string) => {
    // Map icon names to components
    const iconMap: Record<
      string,
      React.ComponentType<{ className?: string }>
    > = {
      Sparkles: Sparkles,
      Blocks: Blocks,
      Barcode: Barcode,
      HousePlug: HousePlug,
      CarFront: CarFront,
      Stethoscope: Stethoscope,
      ChartLine: ChartLine,
    };

    const IconComponent = iconMap[iconName];

    if (IconComponent) {
      return <IconComponent className="h-6 w-6 text-white sm:h-8 sm:w-8" />;
    }
    // Fallback to a default icon if the icon name is not found
    return <Sparkles className="h-6 w-6 text-white sm:h-8 sm:w-8" />;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      backdrop="blur"
      scrollBehavior="inside"
      placement="center"
      classNames={{
        backdrop: 'bg-black/60 backdrop-blur-sm',
        base: 'border border-border/30 bg-background rounded-3xl shadow-2xl max-h-[90vh] flex flex-col',
        header: 'border-b border-border/20 pb-0 shrink-0',
        body: 'p-0 overflow-hidden flex-1',
        closeButton: 'hidden',
      }}
    >
      <ModalContent className="bg-background mx-auto max-h-[85vh] max-w-6xl overflow-hidden border-b border-white/20">
        {/* Custom Close Button */}
        <button
          onClick={onClose}
          aria-label="Close project modal"
          className="absolute top-4 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/20 text-white backdrop-blur-sm transition-all duration-200 hover:border-white/40 hover:bg-black/40"
        >
          <svg
            className="h-8 w-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <ModalHeader className="flex shrink-0 flex-col gap-3 overflow-hidden rounded-t-2xl border border-white/20 p-4 md:p-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex flex-col gap-1">
                <div className="text-xl leading-tight font-bold text-white sm:text-2xl md:text-3xl">
                  {project.title}
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-white/90 sm:text-base md:text-lg">
            {project.description}
          </p>
        </ModalHeader>

        <ModalBody className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted/30 hover:scrollbar-thumb-muted/50 flex-1 overflow-y-auto">
          <div className="space-y-8 p-4 sm:p-6 md:p-8">
            {/* Problem Statement */}
            <section>
              <div
                className={`relative h-36 bg-gradient-to-br ${project.gradient.from} ${project.gradient.to} mb-4 rounded-xl`}
              >
                {/* Dynamic Pattern Based on Project Type */}
                {renderPattern(project.pattern)}

                {/* AI Icon */}
                <div className="relative z-10 flex h-full flex-col items-center justify-center">
                  <div
                    className={`mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br ${project.iconBackground.from} ${project.iconBackground.to} p-4 shadow-lg`}
                  >
                    {renderIcon(project.icon)}
                  </div>

                  {/* Interactive Buttons */}
                  <div className="flex gap-2">
                    <button
                      className={`rounded-full border border-slate-500/30 bg-slate-500/20 px-3 py-1.5 text-xs font-medium transition-colors duration-200 hover:bg-slate-700/80 ${project.textColor}`}
                      aria-label={`Project type: ${project.type}`}
                    >
                      {project.type}
                    </button>
                    <button
                      className={`rounded-full border border-slate-500/30 bg-slate-500/20 px-3 py-1.5 text-xs font-medium transition-colors duration-200 hover:bg-slate-700/80 ${project.textColor}`}
                      aria-label={`Project category: ${project.category}`}
                    >
                      {project.category}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Project Image */}
            {project.image && (
              <section>
                <div className="mt-6 mb-3 flex items-center gap-3">
                  <h2 className="text-foreground text-xl font-bold">
                    Project Architecture
                  </h2>
                </div>
                <div className="bg-muted/20 border-border rounded-xl border p-4">
                  <div className="flex justify-center">
                    <Image
                      src={project.image}
                      alt={`${project.title} architecture diagram`}
                      width={500}
                      height={300}
                      className="rounded-lg shadow-lg"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Project Objective */}
            <section>
              <div className="mt-8 mb-3 flex items-center gap-3">
                <h2 className="text-foreground text-xl font-bold">
                  Project Objective
                </h2>
              </div>
              <div className="bg-muted/30 border-border rounded-xl border p-4 sm:p-6">
                <p className="text-foreground grid gap-4 text-base leading-relaxed sm:text-lg">
                  {project.projectObjective.map((objective, index) => (
                    <span key={index}>{objective}</span>
                  ))}
                </p>
              </div>
            </section>

            {/* Achievements */}
            <section>
              <div className="mt-8 mb-3 flex items-center gap-3">
                <h2 className="text-foreground text-xl font-bold">
                  Key Achievements
                </h2>
              </div>
              <div className="grid gap-4">
                {project.keyAchievements.map((keyAchievements, index) => (
                  <div
                    key={index}
                    className="bg-muted/20 border-border/50 hover:bg-muted/30 flex items-start gap-3 rounded-xl border p-4 transition-colors"
                  >
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                      <span className="text-sm font-bold text-indigo-500 dark:text-indigo-300">
                        {index + 1}
                      </span>
                    </div>
                    <p className="text-foreground leading-relaxed">
                      {keyAchievements}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Technology Implementation */}
            <section>
              <div className="mt-8 mb-3 flex items-center gap-3">
                <h2 className="text-foreground text-xl font-bold">
                  Technologies & Implementation
                </h2>
              </div>
              <div className="bg-muted/20 border-border rounded-xl border p-4">
                <div className="mb-4">
                  <p className="text-foreground leading-relaxed">
                    {project.techParagraph}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {project.technologies.map((tech, index) => {
                    const techIcon = getTechIcon(tech);
                    return (
                      <span
                        key={index}
                        className="from-primary/10 border-primary/20 hover:bg-primary/20 rounded-lg border bg-gradient-to-r to-blue-500/10 px-4 py-2 text-sm font-medium text-blue-400 transition-colors"
                      >
                        {techIcon && (
                          <Image
                            width={16}
                            height={16}
                            src={techIcon}
                            alt={`${tech} icon`}
                            className="mr-2 inline h-4 w-4"
                            onError={e => {
                              (e.target as HTMLImageElement).style.display =
                                'none';
                            }}
                          />
                        )}
                        {tech}
                      </span>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Challenges Faced */}
            <section>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/30 dark:bg-amber-900/20">
                <h2 className="mb-2 inline-flex items-center text-xl font-bold text-amber-800 dark:text-amber-200">
                  <Lock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  <span className="ml-2">Project Confidentiality</span>
                </h2>
                <p className="mt-2 text-lg text-amber-600 dark:text-amber-400">
                  Can only share small parts due to NDA restrictions
                </p>
              </div>
            </section>

            {/* Business Impact */}
            <section className="pt-8">
              <div className="from-primary/10 border-primary/20 rounded-2xl border bg-gradient-to-r to-blue-500/10 p-8">
                <div className="mb-3 flex items-center gap-3">
                  <h2 className="text-foreground text-xl font-bold">
                    Impact & Results
                  </h2>
                </div>
                <p className="gap-`4 grid text-xl leading-relaxed font-semibold text-blue-300">
                  {project.businessImpact.map((impact, index) => (
                    <span key={index}>{impact}</span>
                  ))}
                </p>
              </div>
            </section>
          </div>
        </ModalBody>

        <ModalFooter className="border-border/50 bg-muted/20 shrink-0 border-t px-4 py-4 sm:px-6 sm:py-6">
          <div className="sm:justify-baselin flex w-full flex-col gap-4 sm:flex-row sm:items-center">
            <Link href={project.liveUrl || ''} target="_blank">
              <button
                className="to-primary flex cursor-pointer items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 px-4 py-2 text-sm font-semibold text-white transition-all duration-300 hover:scale-105 sm:px-6 sm:text-base"
                aria-label={`View ${project.title} project live`}
              >
                <span>
                  <ExternalLink className="h-4 w-4" />
                </span>
                <span>View Project</span>
              </button>
            </Link>
            <div className="from-primary border-foreground/30 flex cursor-not-allowed items-center gap-2 rounded-lg border px-4 py-2 text-center text-sm font-semibold sm:text-base">
              <span>
                <Lock className="h-4 w-4" />
              </span>
              <span>NDA Restricted</span>
            </div>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProjectModal;
